import { forwardRef, useImperativeHandle, useRef } from 'react'
import { useGSAP } from '@gsap/react'
import classNames from 'classnames'
import gsap from 'gsap'
import Image from 'next/image'

import logoIcon from '@/assets/logo-icon.png'
import {
  MASK_TRANSITION_ENTER_DURATION,
  MASK_TRANSITION_LEAVE_DURATION,
  MASK_TRANSITION_STAGGER_AMOUNT
} from '@/contexts/transition'

export type MaskTransitionHandler = {
  enterTimeline: () => gsap.core.Timeline
  leaveTimeline: () => gsap.core.Timeline
}

const MaskTransition = forwardRef<MaskTransitionHandler>((_, ref) => {
  const root = useRef<HTMLDivElement>(null)

  const { contextSafe } = useGSAP({ scope: root })

  const enterTimeline = contextSafe(() => {
    return gsap
      .timeline()
      .set(root.current, { display: 'block' })
      .add(
        gsap.timeline().fromTo(
          ['[data-mask-primary]', '[data-mask-secondary]'],
          { yPercent: 100 },
          {
            yPercent: 0,
            duration: MASK_TRANSITION_ENTER_DURATION,
            stagger: MASK_TRANSITION_STAGGER_AMOUNT,
            ease: 'power3.in',
            force3D: true
          }
        )
      )
      .add(
        gsap
          .timeline()
          .fromTo(
            '[data-mask-transparent]',
            { yPercent: 100 },
            {
              yPercent: 0,
              duration: MASK_TRANSITION_ENTER_DURATION,
              ease: 'power3.in',
              force3D: true
            }
          )
          .fromTo(
            '[data-mask-logo]',
            { yPercent: -100 },
            {
              yPercent: 0,
              duration: MASK_TRANSITION_ENTER_DURATION,
              ease: 'power3.in',
              force3D: true
            },
            '<'
          ),
        `<+${MASK_TRANSITION_STAGGER_AMOUNT}`
      )
    /* .fromTo(
        '[data-mask-logo-circle]',
        { strokeDashoffset: length },
        {
          strokeDashoffset: 0,
          duration: MASK_TRANSITION_ENTER_DURATION,
          ease: 'power3.inOut'
        },
        '<'
      ) */
  })

  const leaveTimeline = contextSafe(() => {
    return (
      gsap
        .timeline()
        /* .fromTo(
        '[data-mask-logo-circle]',
        { strokeDashoffset: 0 },
        {
          strokeDashoffset: -length,
          duration: MASK_TRANSITION_ENTER_DURATION,
          ease: 'power3.inOut'
        },
        '<'
      ) */
        .add(
          gsap
            .timeline()
            .fromTo(
              '[data-mask-transparent]',
              { yPercent: 0 },
              {
                yPercent: -100,
                duration: MASK_TRANSITION_LEAVE_DURATION,
                ease: 'power3.out',
                force3D: true
              }
            )
            .fromTo(
              '[data-mask-logo]',
              { yPercent: 0 },
              {
                yPercent: 100,
                duration: MASK_TRANSITION_LEAVE_DURATION,
                ease: 'power3.out',
                force3D: true
              },
              '<'
            ),
          '<'
        )
        .add(
          gsap.timeline().fromTo(
            ['[data-mask-secondary]', '[data-mask-primary]'],
            { yPercent: 0 },
            {
              yPercent: -100,
              duration: MASK_TRANSITION_LEAVE_DURATION,
              stagger: MASK_TRANSITION_STAGGER_AMOUNT,
              ease: 'power3.out',
              force3D: true
            }
          ),
          '<'
        )

        .set(root.current, { display: 'none' })
    )
  })

  useImperativeHandle(
    ref,
    () => ({
      leaveTimeline,
      enterTimeline
    }),
    [enterTimeline, leaveTimeline]
  )

  return (
    <div ref={root} className="fixed left-0 top-0 z-transition h-screen w-full">
      <div
        className="absolute left-0 top-0 h-full w-full bg-text-overline-light"
        data-mask-primary
      />
      <div
        className="absolute left-0 top-0 h-full w-full bg-background-primary-dark"
        data-mask-secondary
      />
      <div
        className="absolute left-0 top-0 h-full w-full overflow-hidden"
        data-mask-transparent
      >
        <div className="absolute left-0 top-0 h-full w-full" data-mask-logo>
          <Image
            src={logoIcon}
            width={181}
            height={94}
            alt="Olio Farchioni"
            className={classNames(
              'absolute left-1/2 top-1/2 h-[4.8rem] w-[4.8rem] -translate-x-1/2 -translate-y-1/2',
              'h-[4rem] w-[7.6rem] md:h-[4.7rem] md:w-[9rem]'
            )}
          />
        </div>
      </div>
    </div>
  )
})

export default MaskTransition
